import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { accessPolicies, institutionDomains as seedDomains, institutionIntegrations, myAcademicRecords, verifiableInstitutions, verificationCases as seedCases } from '../data/verification';
import { addMonths, evaluatePolicy, maskEmail, trustFrom, type PolicyDecision } from '../utils/verification';
import type { AcademicRecord, AccessPolicy, CaseState, InstitutionDomain, StatusHistoryEntry, StudentStatus, TrustRecord, VerificationCase, VerificationLevel } from '../types/verification';
interface VerificationValue {
  institutions: typeof verifiableInstitutions;
  integrations: typeof institutionIntegrations;
  domains: InstitutionDomain[];
  policies: AccessPolicy[];
  records: AcademicRecord[];
  cases: VerificationCase[];

  /** The record the person is currently looking at, and its derived trust. */
  primary: AcademicRecord | null;
  trust: TrustRecord;
  /** The only question a feature asks. Presentation only; services re-evaluate. */
  can: (policyId: string) => PolicyDecision;

  /** Declares or updates a claim. Never touches verification level. */
  declare: (recordId: string, field: 'division' | 'department' | 'programme' | 'level', value: string, reason: string) => void;

  /** Step 1: confirm access to an approved institutional address. */
  verifyEmail: (recordId: string, email: string) => {
    ok: boolean;
    message: string;
  };
  /** Opens a case. A case is the only route to level 2 and above. */
  openCase: (recordId: string, method: 'document' | 'institution_review', evidenceNames: string[]) => string;
  /** Institution or Campuzon decides a case. */
  decideCase: (caseId: string, outcome: 'approved' | 'rejected', reason: string, by: string) => void;
  setCaseState: (caseId: string, state: CaseState, detail: string, by: string) => void;
  /** An appeal opens a NEW case; the original decision is preserved. */
  appeal: (caseId: string, note: string) => string;
  /** Institutional feed reports a status change. */
  reportStatus: (recordId: string, status: StudentStatus, source: string) => void;
  /** Domain registry management. */
  setDomainState: (domainId: string, state: InstitutionDomain['state'], note: string) => void;
}
const Ctx = createContext<VerificationValue | null>(null);
const MY_USER_ID = 'CAMP-2AB19XQ7';
let sequence = 1400;
function historyEntry(input: Omit<StatusHistoryEntry, 'id' | 'at'>): StatusHistoryEntry {
  return {
    id: `hst-${Date.now()}-${sequence++}`,
    at: new Date().toISOString(),
    ...input
  };
}
export function VerificationProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [records, setRecords] = useState<AcademicRecord[]>(myAcademicRecords);
  const [cases, setCases] = useState<VerificationCase[]>(seedCases);
  const [domains, setDomains] = useState<InstitutionDomain[]>(seedDomains);
  const primary = useMemo(() => records.find(record => record.userId === MY_USER_ID && record.kind === 'student') ?? null, [records]);
  const trust = useMemo(() => trustFrom(primary), [primary]);
  const can = useCallback<VerificationValue['can']>(policyId => {
    const policy = accessPolicies.find(entry => entry.id === policyId);
    if (!policy) {
      return {
        allowed: false,
        message: 'No policy is configured for this feature, so access is refused.',
        remedy: null
      };
    }
    return evaluatePolicy(policy, trust, primary?.status ?? null);
  }, [primary, trust]);
  const patchRecord = useCallback((recordId: string, next: (record: AcademicRecord) => AcademicRecord) => {
    setRecords(current => current.map(record => record.id === recordId ? next(record) : record));
  }, []);
  const declare = useCallback<VerificationValue['declare']>((recordId, field, value, reason) => {
    patchRecord(recordId, record => {
      const existing = record[field];
      if (!existing) return record;
      // A declared change to a field an institution established does NOT
      // overwrite the source. It raises a conflict for review instead.
      const wasInstitutional = existing.source !== 'declared';
      return {
        ...record,
        [field]: wasInstitutional ? existing : {
          value,
          source: 'declared' as const,
          confirmedAt: null
        },
        verification: wasInstitutional ? {
          ...record.verification,
          state: 'requires_reverification' as const
        } : record.verification,
        history: [historyEntry({
          field,
          before: existing.value,
          after: value,
          source: 'declared',
          changedBy: '@saviour',
          reason: wasInstitutional ? `${reason} — the institution-sourced value is retained and the record is flagged for review.` : reason,
          verificationAtTime: record.verification.state
        }), ...record.history],
        updatedAt: new Date().toISOString()
      };
    });
  }, [patchRecord]);
  const verifyEmail = useCallback<VerificationValue['verifyEmail']>((recordId, email) => {
    const record = records.find(entry => entry.id === recordId);
    if (!record) return {
      ok: false,
      message: 'No such academic record.'
    };
    const domainPart = email.split('@')[1]?.toLowerCase() ?? '';
    const match = domains.find(entry => entry.domain.toLowerCase() === domainPart && entry.institutionId === record.institutionId);
    if (!match) {
      return {
        ok: false,
        message: 'That domain is not on the approved register for this institution. A domain is never trusted because someone typed it — ask your institution to have it registered.'
      };
    }
    if (match.state !== 'approved') {
      return {
        ok: false,
        message: `${match.domain} is on the register but its state is “${match.state.replace('_', ' ')}”. It cannot be used for verification until ownership is established.`
      };
    }
    const now = new Date().toISOString();
    const institution = verifiableInstitutions.find(entry => entry.id === record.institutionId);
    const months = institution?.expiryMonths.institutional_email ?? 12;
    patchRecord(recordId, entry => {
      // Email verification never raises the level past level 1, and never
      // touches status — access to a mailbox is not enrolment.
      const higher = entry.verification.level === 'institution_verified' || entry.verification.level === 'institution_integrated' || entry.verification.level === 'document_verified';
      return {
        ...entry,
        verification: higher ? {
          ...entry.verification,
          emailMasked: maskEmail(email)
        } : {
          level: 'email_verified' as VerificationLevel,
          state: 'verified' as const,
          method: 'institutional_email' as const,
          source: `${institution?.name ?? 'Institution'} — approved email domain ${match.domain}`,
          meaning: 'Access to an approved institutional email address was confirmed. This does not confirm current enrolment, programme or level.',
          verifiedAt: now,
          expiresAt: addMonths(now, months),
          lastRevalidatedAt: now,
          caseId: null,
          emailMasked: maskEmail(email)
        },
        history: [historyEntry({
          field: 'Verification level',
          before: entry.verification.level,
          after: higher ? entry.verification.level : 'email_verified',
          source: 'system',
          changedBy: 'Verification engine',
          reason: `Access to ${maskEmail(email)} confirmed by single-use code`,
          verificationAtTime: 'verified'
        }), ...entry.history],
        updatedAt: now
      };
    });
    return {
      ok: true,
      message: 'Institutional email verified. This confirms you can receive mail at that address — it does not yet confirm you are enrolled.'
    };
  }, [domains, patchRecord, records]);
  const openCase = useCallback<VerificationValue['openCase']>((recordId, method, evidenceNames) => {
    const record = records.find(entry => entry.id === recordId);
    const id = `VER-2026-00${sequence++}`;
    if (!record) return id;
    const now = new Date().toISOString();
    setCases(current => [{
      id,
      userId: record.userId,
      handle: 'saviour',
      displayName: 'Saviour Adeyemi',
      institutionId: record.institutionId,
      campusId: record.campusId,
      method,
      requestedLevel: method === 'document' ? 'document_verified' : 'institution_verified',
      claimed: {
        programme: record.programme.value,
        level: record.level?.value ?? null,
        status: record.status
      },
      state: 'submitted',
      priority: 'normal',
      submittedAt: now,
      assignedTo: null,
      evidence: evidenceNames.map((filename, index) => ({
        id: `ev-${Date.now()}-${index}`,
        kind: 'Submitted document',
        filename,
        reference: `doc://secure/${Math.random().toString(16).slice(2, 8)}`,
        submittedAt: now,
        restricted: true
      })),
      decision: {
        outcome: null,
        by: null,
        at: null,
        reason: null
      },
      internalNotes: [],
      events: [{
        id: `cev-${Date.now()}`,
        at: now,
        actor: '@saviour',
        action: 'Case opened',
        detail: method === 'document' ? 'Requested document verification.' : 'Requested confirmation directly from the institution.'
      }],
      appealOfCaseId: null,
      riskSignals: [],
      conflict: null
    }, ...current]);
    patchRecord(recordId, entry => ({
      ...entry,
      verification: {
        ...entry.verification,
        state: 'pending_verification',
        caseId: id
      },
      history: [historyEntry({
        field: 'Verification state',
        before: entry.verification.state,
        after: 'pending_verification',
        source: 'system',
        changedBy: '@saviour',
        reason: `Case ${id} opened using ${method === 'document' ? 'academic documents' : 'institution review'}`,
        verificationAtTime: entry.verification.state
      }), ...entry.history],
      updatedAt: now
    }));
    return id;
  }, [patchRecord, records]);
  const setCaseState = useCallback<VerificationValue['setCaseState']>((caseId, state, detail, by) => {
    const now = new Date().toISOString();
    setCases(current => current.map(entry => entry.id === caseId ? {
      ...entry,
      state,
      assignedTo: state === 'under_review' ? by : entry.assignedTo,
      events: [...entry.events, {
        id: `cev-${Date.now()}`,
        at: now,
        actor: by,
        action: `Moved to ${state.replace('_', ' ')}`,
        detail
      }]
    } : entry));
  }, []);
  const decideCase = useCallback<VerificationValue['decideCase']>((caseId, outcome, reason, by) => {
    const now = new Date().toISOString();
    const target = cases.find(entry => entry.id === caseId);
    setCases(current => current.map(entry => entry.id === caseId ? {
      ...entry,
      state: outcome === 'approved' ? 'approved' : 'rejected',
      decision: {
        outcome,
        by,
        at: now,
        reason
      },
      events: [...entry.events, {
        id: `cev-${Date.now()}`,
        at: now,
        actor: by,
        action: outcome === 'approved' ? 'Approved' : 'Rejected',
        detail: reason
      }]
    } : entry));
    if (!target) return;
    const record = records.find(entry => entry.verification.caseId === caseId);
    if (!record) return;
    const institution = verifiableInstitutions.find(entry => entry.id === record.institutionId);
    const method = target.method;
    const months = institution?.expiryMonths[method] ?? 12;
    patchRecord(record.id, entry => ({
      ...entry,
      verification: outcome === 'approved' ? {
        level: target.requestedLevel,
        state: 'verified',
        method,
        source: method === 'institution_review' ? `${institution?.name ?? 'Institution'} — ${by}` : 'Campuzon verification team',
        meaning: method === 'institution_review' ? 'An authorised representative of the institution confirmed this affiliation directly.' : 'Academic documentation was reviewed and accepted by the Campuzon verification team. This is not an institutional confirmation.',
        verifiedAt: now,
        expiresAt: addMonths(now, months),
        lastRevalidatedAt: now,
        caseId,
        emailMasked: entry.verification.emailMasked
      } : {
        ...entry.verification,
        state: 'rejected'
      },
      history: [historyEntry({
        field: 'Verification level',
        before: entry.verification.level,
        after: outcome === 'approved' ? target.requestedLevel : entry.verification.level,
        source: method === 'institution_review' ? 'institution' : 'document',
        changedBy: by,
        reason,
        verificationAtTime: outcome === 'approved' ? 'verified' : 'rejected'
      }), ...entry.history],
      updatedAt: now
    }));
  }, [cases, patchRecord, records]);
  const appeal = useCallback<VerificationValue['appeal']>((caseId, note) => {
    const original = cases.find(entry => entry.id === caseId);
    const id = `VER-2026-00${sequence++}`;
    if (!original) return id;
    const now = new Date().toISOString();
    setCases(current => [{
      ...original,
      id,
      state: 'submitted',
      submittedAt: now,
      assignedTo: null,
      decision: {
        outcome: null,
        by: null,
        at: null,
        reason: null
      },
      internalNotes: [],
      appealOfCaseId: caseId,
      events: [{
        id: `cev-${Date.now()}`,
        at: now,
        actor: `@${original.handle}`,
        action: 'Appeal opened',
        detail: `${note} The original decision on ${caseId} remains on record.`
      }]
    }, ...current]);
    return id;
  }, [cases]);
  const reportStatus = useCallback<VerificationValue['reportStatus']>((recordId, status, source) => {
    const now = new Date().toISOString();
    patchRecord(recordId, entry => ({
      ...entry,
      status,
      statusSource: 'integration',
      // Graduation preserves the verified relationship; it does not remove it.
      verification: status === 'graduated' || status === 'alumni' ? {
        ...entry.verification,
        lastRevalidatedAt: now
      } : entry.verification,
      history: [historyEntry({
        field: 'Student status',
        before: entry.status,
        after: status,
        source: 'integration',
        changedBy: source,
        reason: 'Reported by the institutional connection',
        verificationAtTime: entry.verification.state
      }), ...entry.history],
      updatedAt: now
    }));
  }, [patchRecord]);
  const setDomainState = useCallback<VerificationValue['setDomainState']>((domainId, state, note) => {
    setDomains(current => current.map(entry => entry.id === domainId ? {
      ...entry,
      state,
      note,
      reviewedBy: '@you'
    } : entry));
  }, []);
  const value = useMemo<VerificationValue>(() => ({
    institutions: verifiableInstitutions,
    integrations: institutionIntegrations,
    domains,
    policies: accessPolicies,
    records,
    cases,
    primary,
    trust,
    can,
    declare,
    verifyEmail,
    openCase,
    decideCase,
    setCaseState,
    appeal,
    reportStatus,
    setDomainState
  }), [appeal, can, cases, decideCase, declare, domains, openCase, primary, records, reportStatus, setCaseState, setDomainState, trust, verifyEmail]);
  return <Ctx.Provider value={value} data-id="element-5543" data-mp="b2hajt99vdhc">{children}</Ctx.Provider>;
}
export function useVerification(): VerificationValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useVerification must be used inside VerificationProvider');
  return value;
}